<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import AuthLayout from '@/components/layout/AuthLayout.vue'
import { useRouter } from 'vue-router'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import GlobalFormMessage from '@/components/forms/GlobalFormMessage.vue'
import { EnvelopeIcon } from '@/icons'
import CheckBoxDefault from '@/components/forms/Checkboxes/CheckBoxDefault.vue'
import { useGlobalFormMessages } from '@/composables/useGlobalFormMessages'
import { useAuthStore } from '@/stores/auth'
import { setUserType, getUserType } from '@/utils/roleHandler'  // Changed from setUserRole

const router = useRouter()

const email = ref('')
const password = ref('')
const keepLoggedIn = ref(false)
const isSubmitting = ref(false)
const isDevMode = ref(false)

const authStore = useAuthStore()

// Use the form errors composable
const {
  showErrors,
  addError,
  clearErrors,
  removeError,
  mainError,
  showValidationErrors
} = useGlobalFormMessages()

// Check if we're in development mode
onMounted(() => {
  isDevMode.value = import.meta.env.DEV

  // Check if already logged in
  if (authStore.isAuthenticated) {
    const userType = authStore.user?.userType || getUserType()
    console.log('👤 Already logged in as:', userType)
    router.push('/dashboard')
  }
})

// Computed properties for error messages
const emailError = computed(() => {
  if (!showErrors.value) return ''

  if (!email.value) {
    return 'Email address is required'
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email.value)) {
    return 'Please enter a valid email address'
  }

  return ''
})

const passwordError = computed(() => {
  if (!showErrors.value) return ''

  if (!password.value) {
    return 'Password is required'
  }

  if (password.value.length < 6) {
    return 'Password must be at least 6 characters'
  }

  return ''
})

// Form validation
const isFormValid = computed(() => {
  return !emailError.value && !passwordError.value
})

const handleSubmit = async () => {
  showValidationErrors()
  clearErrors()

  if (!isFormValid.value) {
    addError({
      message: 'Please fix the above errors to proceed further.',
      type: 'error'
    })
    return
  }

  isSubmitting.value = true

  try {
    console.log('🔐 Attempting login...')

    // Use the auth store's login method
    const result = await authStore.login(email.value, password.value, keepLoggedIn.value)

    console.log('✅ Login successful:', {
      token: result.token ? 'Token received' : 'No token',
      user: result.user
    })

    // Check if we have user data
    if (!result.user) {
      throw new Error('No user data received from server')
    }

    // Set userType
    const userType = result.user.userType
    if (userType) {
      setUserType(userType)
      console.log('🎭 User type set to:', userType)
    } else {
      console.warn('⚠️ No userType found in user data')
    }

    // Wait a moment for store to update
    await new Promise(resolve => setTimeout(resolve, 100))

    // Verify auth store state
    console.log('📊 Auth store state after login:', {
      isAuthenticated: authStore.isAuthenticated,
      user: authStore.user,
      userType: authStore.userType
    })

    // Redirect to dashboard
    router.push('/dashboard')

  } catch (error: unknown) {
    console.error('❌ Login error:', error)

    let errorMessage = 'Invalid email or password. Please try again.'

    if (error instanceof Error) {
      // Extract error message
      const errorText = error.message.toLowerCase()

      if (errorText.includes('network') || errorText.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection.'
      } else if (errorText.includes('401') || errorText.includes('unauthorized')) {
        errorMessage = 'Invalid credentials. Please check your email and password.'
      } else if (errorText.includes('404')) {
        errorMessage = 'Server not found. Please contact administrator.'
      } else if (errorText.includes('403') || errorText.includes('forbidden')) {
        errorMessage = 'Access denied. Your account may be inactive or suspended.'
      } else {
        errorMessage = error.message
      }
    }

    addError({
      message: errorMessage,
      type: 'error'
    })
  } finally {
    isSubmitting.value = false
  }
}

// Demo credentials helper (for testing - remove in production)
const fillDemoCredentials = (type: string) => {
  clearErrors()

  switch(type) {
    case 'admin':
      email.value = 'admin@pokasu.com'
      password.value = 'Admin@123'
      break
    case 'agent':
      email.value = 'anandrathod09@gmail.com'
      password.value = 'R@jayshree09'
      break
    case 'engineer':
      email.value = 'sanjay.verma@example.com'
      password.value = 'Engineer@123'
      break
    case 'client':
      email.value = 'client@tender.com'
      password.value = 'R@jayshree09'
      break
    default:
      email.value = 'test@example.com'
      password.value = 'password'
  }

  // Auto-submit after filling for demo
  if (isDevMode.value) {
    setTimeout(() => handleSubmit(), 500)
  }
}
</script>

<template>
  <AuthLayout
    title="Welcome back!"
    description="Welcome back! Please enter your details."
  >
    <form @submit.prevent="handleSubmit">
      <div class="space-y-5">
        <!-- Demo Credentials Panel (remove in production) -->
        <div v-if="isDevMode" class="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Demo Credentials (click to auto-fill)
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              @click="fillDemoCredentials('admin')"
              class="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 transition-colors"
            >
              Admin
            </button>
            <button
              type="button"
              @click="fillDemoCredentials('agent')"
              class="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 dark:bg-green-900 dark:text-green-300 transition-colors"
            >
              Agent
            </button>
            <button
              type="button"
              @click="fillDemoCredentials('engineer')"
              class="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 transition-colors"
            >
              Engineer
            </button>
            <button
              type="button"
              @click="fillDemoCredentials('client')"
              class="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-300 transition-colors"
            >
              Client
            </button>
          </div>
        </div>

        <!-- Email Input -->
        <TextInputDefault
          labelText="Email"
          placeholder="Enter your Email"
          id="loginEmail"
          name="loginemail"
          type="text"
          v-model="email"
          iconPosition="right"
          asterisk
          :error-message="emailError"
          :error-type="emailError ? 'error' : 'info'"
        >
          <template #icon>
            <EnvelopeIcon />
          </template>
        </TextInputDefault>

        <!-- Password Input -->
        <TextInputDefault
          labelText="Password"
          placeholder="Enter your password"
          id="password"
          name="password"
          type="password"
          v-model="password"
          icon="eye"
          asterisk
          :error-message="passwordError"
          :error-type="passwordError ? 'error' : 'info'"
        />

        <!-- Remember Me & Forgot Password -->
        <div class="flex items-center justify-between">
          <CheckBoxDefault
            label="Keep me logged in"
            v-model="keepLoggedIn"
            error-type="warning"
          />

          <router-link
            to="/forgot-password"
            class="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400 transition-colors"
          >
            Forgot password?
          </router-link>
        </div>

        <!-- Global Form Error/Success Messages -->
        <div class="space-y-2">
          <GlobalFormMessage
            v-if="mainError"
            :message="mainError.message"
            :type="mainError.type"
            @dismiss="removeError(mainError!.message)"
          />

          <!-- Auth store errors -->
          <GlobalFormMessage
            v-if="authStore.error && !mainError"
            :message="authStore.error"
            type="error"
            @dismiss="authStore.error = null"
          />

          <!-- Success message -->
          <GlobalFormMessage
            v-if="authStore.isAuthenticated && !authStore.error && !mainError"
            message="Login successful! Redirecting..."
            type="success"
          />
        </div>

        <!-- Submit Button -->
        <BaseButton
          type="submit"
          :text="isSubmitting ? 'Signing In...' : 'Sign In'"
          variant="primary"
          size="md"
          :disabled="isSubmitting"
          :class="['w-full', isSubmitting ? 'opacity-75 cursor-not-allowed' : '']"
        />


      </div>
    </form>
  </AuthLayout>
</template>
